import {
  BodyText,
  Card,
  Badge,
} from '@aaimplatform/ui';
import {
  CheckCircle2 as HealthyIcon,
  AlertTriangle as WarningIcon,
  AlertCircle as ErrorIcon,
  Database as DatabaseIcon,
  Cpu as MemoryIcon,
  Cloud as QueueIcon,
  Server as ServiceIcon,
  RefreshCcw as RefreshIcon,
} from 'lucide-react';
import { useSystemHealth } from '../hooks/useSystemHealth';
import type { ServiceStatusEntry } from '../api/client';

const INFRA_SERVICE_NAMES = new Set([
  'postgresql',
  'postgres',
  'redis',
  'redis cache',
  'kafka',
  'database',
]);

function isInfrastructure(entry: ServiceStatusEntry): boolean {
  if (!entry.service) return false;
  return INFRA_SERVICE_NAMES.has(entry.service.toLowerCase());
}

function getStatusIcon(status: ServiceStatusEntry['status'] | undefined) {
  if (status === 'HEALTHY') return <HealthyIcon className="text-growth" />;
  if (status === 'DEGRADED') return <WarningIcon className="text-warning" />;
  return <ErrorIcon className="text-error" />;
}

function getStatusVariant(
  status: ServiceStatusEntry['status'] | undefined,
): 'success' | 'warning' | 'error' {
  if (status === 'HEALTHY') return 'success';
  if (status === 'DEGRADED') return 'warning';
  return 'error';
}

function deriveOverall(services: ServiceStatusEntry[]): NonNullable<ServiceStatusEntry['status']> {
  if (services.length === 0) return 'DEGRADED';
  if (services.some((s) => s.status === 'DOWN')) return 'DOWN';
  if (services.some((s) => s.status === 'DEGRADED')) return 'DEGRADED';
  return 'HEALTHY';
}

function formatPct(value: number | undefined, digits = 1): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '--';
  return `${value.toFixed(digits)}%`;
}

function formatMs(value: number | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '--';
  return `${Math.round(value)}ms`;
}

function getInfraIcon(serviceName: string | undefined) {
  const lower = serviceName?.toLowerCase() ?? '';
  if (lower.includes('postgres') || lower.includes('database')) {
    return <DatabaseIcon className="text-accent" />;
  }
  if (lower.includes('redis')) {
    return <MemoryIcon className="text-accent-secondary" />;
  }
  return <QueueIcon className="text-tertiary" />;
}

export function SystemHealthPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useSystemHealth();

  if (isLoading) {
    return (
      <div>
        <Card className="p-8">
          <BodyText variant="ui" className="text-tertiary">Loading system health...</BodyText>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <Card className="p-6 bg-error/10 border border-error/20">
          <BodyText variant="ui" className="text-error font-semibold mb-2">
            Unable to load system health dashboard
          </BodyText>
          <BodyText variant="ui" className="text-error text-sm mb-4">
            {error?.message ?? 'The health dashboard endpoint did not respond.'}
          </BodyText>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 text-sm text-error underline"
          >
            <RefreshIcon className="w-4 h-4" /> Retry
          </button>
        </Card>
      </div>
    );
  }

  const services = data.services ?? [];
  const applicationServices = services.filter((s) => !isInfrastructure(s));
  const infrastructureServices = services.filter(isInfrastructure);
  const overallHealth = deriveOverall(services);
  const degradedOrDown = services.filter((s) => s.status !== 'HEALTHY');

  const dbPool = data.dbConnectionPool;
  const dbPoolMax = data.dbConnectionPoolMax;
  const dbPoolDisplay =
    dbPool !== undefined && dbPoolMax !== undefined && dbPoolMax > 0
      ? `${dbPool} / ${dbPoolMax}`
      : dbPool !== undefined
        ? String(dbPool)
        : '--';

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 text-sm text-tertiary hover:text-primary disabled:opacity-50"
            aria-label="Refresh system health"
          >
            <RefreshIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Refreshing' : 'Refresh'}
          </button>
          <Badge variant={getStatusVariant(overallHealth)} icon={getStatusIcon(overallHealth)}>
            Overall: {overallHealth}
          </Badge>
        </div>
      </div>

      {degradedOrDown.length > 0 && (
        <Card className={`p-4 mb-6 ${overallHealth === 'DOWN' ? 'bg-error/10 border border-error/20' : 'bg-warning/10 border border-warning/20'}`}>
          <BodyText variant="ui" className={overallHealth === 'DOWN' ? 'text-error' : 'text-warning'}>
            {degradedOrDown
              .map((s) => `${s.service ?? 'unknown service'} is ${s.status}`)
              .join('. ')}
          </BodyText>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">CPU Usage</BodyText>
          <BodyText variant="ui" className="text-2xl font-semibold mb-2">
            {formatPct(data.cpuUtilization, 0)}
          </BodyText>
          <div className="w-full h-2 bg-recessed rounded overflow-hidden">
            <div
              className={`h-full ${(data.cpuUtilization ?? 0) > 80 ? 'bg-error' : 'bg-accent'}`}
              style={{ width: `${Math.min(data.cpuUtilization ?? 0, 100)}%` }}
            />
          </div>
        </Card>

        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">Memory</BodyText>
          <BodyText variant="ui" className="text-2xl font-semibold mb-2">
            {formatPct(data.memoryUsage, 0)}
          </BodyText>
          <div className="w-full h-2 bg-recessed rounded overflow-hidden">
            <div
              className={`h-full ${(data.memoryUsage ?? 0) > 80 ? 'bg-error' : 'bg-accent'}`}
              style={{ width: `${Math.min(data.memoryUsage ?? 0, 100)}%` }}
            />
          </div>
        </Card>

        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">DB Connections</BodyText>
          <BodyText variant="ui" className="text-2xl font-semibold">{dbPoolDisplay}</BodyText>
        </Card>

        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">P99 Response</BodyText>
          <BodyText variant="ui" className="text-2xl font-semibold">
            {formatMs(data.avgResponseTimeP99)}
          </BodyText>
        </Card>

        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">API Success</BodyText>
          <BodyText
            variant="ui"
            className={`text-2xl font-semibold ${(data.apiSuccessRate ?? 100) < 99 ? 'text-warning' : 'text-growth'}`}
          >
            {formatPct(data.apiSuccessRate, 2)}
          </BodyText>
        </Card>

        <Card className="p-4">
          <BodyText variant="ui" className="text-xs text-tertiary mb-1">Webhook Success</BodyText>
          <BodyText
            variant="ui"
            className={`text-2xl font-semibold ${(data.webhookSuccessRate ?? 100) < 99 ? 'text-warning' : 'text-growth'}`}
          >
            {formatPct(data.webhookSuccessRate, 2)}
          </BodyText>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="p-6">
            <BodyText variant="ui" className="font-semibold mb-4">Services</BodyText>
            {applicationServices.length === 0 ? (
              <BodyText variant="ui" className="text-xs text-tertiary">
                No application services reporting.
              </BodyText>
            ) : (
              <div className="space-y-3">
                {applicationServices.map((service) => (
                  <div key={service.service} className="flex items-center gap-3 py-2">
                    <ServiceIcon className="text-tertiary w-4 h-4" />
                    <div className="flex-1">
                      <BodyText variant="ui" className="font-medium text-sm">
                        {service.service ?? 'unknown'}
                      </BodyText>
                      <BodyText variant="ui" className="text-xs text-tertiary">
                        {service.uptime ?? '--'} uptime
                        {service.lastCheck ? ` | checked ${service.lastCheck}` : ''}
                      </BodyText>
                    </div>
                    <Badge variant={getStatusVariant(service.status)} size="sm">
                      {service.status ?? 'UNKNOWN'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <BodyText variant="ui" className="font-semibold mb-4">Infrastructure</BodyText>
            {infrastructureServices.length === 0 ? (
              <BodyText variant="ui" className="text-xs text-tertiary">
                No infrastructure services reporting.
              </BodyText>
            ) : (
              <div className="space-y-3">
                {infrastructureServices.map((service) => (
                  <div key={service.service} className="flex items-center gap-3 py-2">
                    {getInfraIcon(service.service)}
                    <div className="flex-1">
                      <BodyText variant="ui" className="font-medium text-sm">
                        {service.service ?? 'unknown'}
                      </BodyText>
                      <BodyText variant="ui" className="text-xs text-tertiary">
                        {service.uptime ?? '--'} uptime
                        {service.lastCheck ? ` | checked ${service.lastCheck}` : ''}
                      </BodyText>
                    </div>
                    <Badge variant={getStatusVariant(service.status)} size="sm">
                      {service.status ?? 'UNKNOWN'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {data.uptime30d !== undefined && (
        <Card className="mt-6 p-4">
          <BodyText variant="ui" className="text-xs text-tertiary">
            30-day uptime: {formatPct(data.uptime30d, 2)}
          </BodyText>
        </Card>
      )}
    </div>
  );
}
